import Cache from '@fs/zion-cache'
import type { PersonId, RelationshipId, RelationshipType } from '@fs/zion-tree-types'
import axios from '@fs/zion-axios'
import { trackEvent } from '@fs/zion-analytics'
import type { Note } from './notes'
import type { SwrResponse } from './useSwrRequest'
import { fetcher, useSwrRequest, updateRequestMultiple } from './useSwrRequest'

const getCache = new Cache({ storeName: 'notes_r9', encrypted: process.env.NODE_ENV === 'production' })

export type NoteType = RelationshipType | 'person'

export type NotesResponse = SwrResponse<Note[]> & { notes?: Note[] }

const urlGetAll = (url: string): string => `${url}/all`
const urlGetOne = (url: string, id: string): string => `${url}/${id}`
export const NOTE_ERROR = ':error unable to read note text:'

/*
 * Implementation note:
 * readNotes handles 'couple', 'parent-child', 'parent', and 'association' notes.
 * However, since the 'association' endpoints uses the delete method to delete,
 * whereas the other ones use a put response with a /delete in the url. So this code needs to handle them differently.
 * Since only the URL is passed in, it needs to be checked for the word 'association' to distinguish it.
 */

function isNotAssociation(url: string): boolean {
  return !url.includes('association')
}

/**
 * Reads notes from tree-data backend and converts it to the format required by the frontend.
 * The `url` should be of the form:
 *   `/service/tree/tree-data/relationship/couple/${containerId}/notes`
 */
export async function readNotes(url: string, numberOfNotesToReturn: number, cache?: Cache): Promise<Note[]> {
  if (numberOfNotesToReturn === 0) return []
  const res = await fetcher<{ notes: Note[] }>(urlGetAll(url), cache)
  const titlesOnlyNotes = res?.notes
  if (!titlesOnlyNotes || titlesOnlyNotes.length === 0) return []

  const notesToShowTitleOnlyNotes = (titlesOnlyNotes ?? [])
    .sort((note) => (note.cautionary ? -1 : 0))
    .slice(0, numberOfNotesToReturn)

  const reqs = notesToShowTitleOnlyNotes.map((note) => fetcher<Note>(urlGetOne(url, note.id), cache))
  const notes = await Promise.allSettled(reqs).then(
    (results) =>
      results
        .map((result) => {
          if (result.status === 'fulfilled') {
            return result.value
          }
          const urlArray = result?.reason?.config?.url?.split('/') ?? []
          return { text: NOTE_ERROR, id: urlArray[urlArray.length - 1] }
        })
        .filter(Boolean) as Note[]
  )

  // Finally, with all the data in hand, we can merge the results
  mergeNotes(titlesOnlyNotes, notes)
  return mergeNotes(titlesOnlyNotes, notes)
}

function mergeNotes(fullNotes: Note[], noteDetails: Note[]): Note[] {
  fullNotes.forEach((note) => {
    const details = noteDetails.find((n) => n.id === note.id)
    if (details?.text === NOTE_ERROR) {
      note.text = details.text
    } else if (details) {
      note.title = details.title
      note.contributor = details.contributor
      note.justification = details.justification
      note.text = details.text
    }
  })
  return fullNotes
}

export const notesUrl = (noteType: NoteType, containerId: RelationshipId | PersonId | undefined): string => {
  // For everything but 'person', the path is 'relationship/${noteType}'. For 'person' it's just 'person'.
  const prefix = noteType === 'person' ? '' : 'relationship/'
  const version = noteType === 'association' ? '' : 'v8/'
  return `/service/tree/tree-data/${version}${prefix}${noteType}/${containerId}/notes`
}

/**
 * Get all notes of a given type.
 */
export function useNotes(
  noteType: NoteType,
  containerId: RelationshipId | PersonId | undefined,
  numberOfNotesToReturn: number
): NotesResponse {
  const url = containerId ? notesUrl(noteType, containerId) : null
  const response = useSwrRequest<Note[]>(url, getCache, {
    emulator: (eUrl, cache) => readNotes(eUrl, numberOfNotesToReturn, cache),
  })
  return { ...response, notes: response.data }
}

export type UnsavedNote = Omit<Note, 'id'>

type KeysToMutate = (
  | string
  | { key: string; newData: (currentNotes: Note[] | undefined) => Promise<Note[] | undefined> }
)[]

function mutateKeys(
  noteType: NoteType,
  containerId: RelationshipId | PersonId,
  optimisticMutator: (currentNotes: Note[] | undefined) => Promise<Note[] | undefined>
): KeysToMutate {
  return [{ key: notesUrl(noteType, containerId), newData: optimisticMutator }]
}

export async function addNote(
  noteType: NoteType,
  containerId: RelationshipId | PersonId,
  note: UnsavedNote
): Promise<Note> {
  const url = notesUrl(noteType, containerId)

  async function optimisticMutator(currentNotes: Note[] | undefined): Promise<Note[]> {
    const newNote = { ...note, id: `${Date.now()}` }
    return currentNotes ? [newNote, ...currentNotes] : [newNote]
  }

  return updateRequestMultiple(mutateKeys(noteType, containerId, optimisticMutator), async () => axios.post(url, note))
}

export async function updateNote(
  noteType: NoteType,
  containerId: RelationshipId | PersonId,
  note: Note
): Promise<Note> {
  if (!note.id) throw new Error('Note must have an id')
  const url = `${notesUrl(noteType, containerId)}/${note.id}`

  async function optimisticMutator(currentNotes: Note[] | undefined): Promise<Note[] | undefined> {
    if (!currentNotes) return currentNotes
    const noteIndex = currentNotes.findIndex((n) => n.id === note.id)
    const newNotes = [...currentNotes]
    newNotes[noteIndex] = note
    return newNotes
  }

  return updateRequestMultiple(mutateKeys(noteType, containerId, optimisticMutator), async () => axios.put(url, note))
}

export async function deleteNote(
  noteType: NoteType,
  containerId: RelationshipId | PersonId,
  note: Note
): Promise<void> {
  if (!note.id) throw new Error('Note must have and id')
  const url = `${notesUrl(noteType, containerId)}/${note.id}`

  async function optimisticMutator(currentNotes: Note[] | undefined): Promise<Note[] | undefined> {
    if (!currentNotes) return currentNotes
    const noteIndex = currentNotes.findIndex((n) => n.id === note.id)
    return currentNotes.slice(0, noteIndex).concat(currentNotes.slice(noteIndex + 1))
  }

  await updateRequestMultiple(mutateKeys(noteType, containerId, optimisticMutator), () => {
    if (isNotAssociation(url)) return axios.put(`${url}/delete`, { reason: note.justification })
    return axios.delete(url, { data: { reason: note.justification } })
  })

  trackEvent({ event_name: 'Deleted Note' })
}
